// Department inference on capture (v0.1.242, Lj Oct 11: "it would be super slick if as we made tasks it assigned them
// to departments based on their nature"). Rules + on-device learning only — no AI in this app.
//
// The order, first that answers wins:
//   1. lock      — the project is locked to one department (Project settings, v0.1.230)                → high
//   2. given     — the task already carries one (a template's, or picked by hand on the chip)             → high
//   3. home      — the project says "new tasks here go to its home department" (newTaskDepartment)       → high
//   4. tags      — #bug / #deploy → Dev, #callback / #materials → Field, #receipt → Finance, #lead → Sales,
//                  #content / #reel → Marketing (DEPT_TAG_ROLES; a suite tag's kind: personal → Personal,
//                  trade / equipment → Field)                                                              → high
//   5. words     — the title's words: what YOU picked for a word before (meta/deptLearn, learned from your picks
//                  and corrections) beats the starter lexicon (DEPT_LEXICON) for that word; the departments
//                  the words point at are scored — one clear winner = medium (high with ≥ 2 words or a word
//                  you've confirmed ≥ 3 times), a contest = low
//   6. fallback  — the department you're in (medium), else the project's home (low), else General (low)
// A department is matched to a role by its name (deptgoals.deptRole: Sales, Marketing, Finance, Dev, Field, Personal).
// Pure — the caller reads meta/deptLearn and commits the writes.
import { GENERAL_DEPARTMENT_ID, listDepartments } from './hierarchy.js';
import { titleTokens } from './autotag.js';
import { canonTag, tagIndex, resolveTag } from './tags.js';
import { deptRole } from './deptgoals.js';
import { isDepartmentLocked } from './taskdepartments.js';
import { merge, set } from './commands/writes.js';

export const DEPT_LEARN_PATH = 'meta/deptLearn';
export const DEPT_GUESS_CONFIDENCE = ['high', 'medium', 'low'];

// Tag slug → role. The score tags (tagusage SCORE_TAG_ROLES) plus the ones Lj named.
export const DEPT_TAG_ROLES = Object.freeze({
  bug: 'dev', shipped: 'dev', improvement: 'dev', deploy: 'dev', feature: 'dev',
  callback: 'field', materials: 'field', material: 'field', travel: 'field', framing: 'field', trim: 'field',
  receipt: 'finance', receipts: 'finance', invoice: 'finance', gst: 'finance', payroll: 'finance', bookkeeping: 'finance',
  lead: 'sales', leads: 'sales', estimate: 'sales', quote: 'sales', assessment: 'sales', bid: 'sales', 'follow-up': 'sales',
  content: 'marketing', reel: 'marketing', post: 'marketing', ad: 'marketing', flier: 'marketing', campaign: 'marketing', video: 'marketing',
  personal: 'personal', home: 'personal',
});
const KIND_ROLES = { personal: 'personal', trade: 'field', equipment: 'field' };

// The starter lexicon: role → title words. Small and generic, like autotag's STARTER_TAG_RULES — it only starts
// things off; your own picks take over word by word. Written as plain words, matched in titleTokens form.
export const DEPT_LEXICON = Object.freeze({
  finance: ['invoice', 'invoices', 'receipt', 'receipts', 'gst', 'pst', 'payroll', 'paystub', 'bookkeeping', 'books', 'reconcile', 'reconciling',
    'tax', 'taxes', 'bank', 'expense', 'expenses', 'remittance', 'remit', 'cra', 'ledger', 'statement', 'accountant', 'budget', 'deposit', 'payment', 'bill', 'bills', 'wages'],
  sales: ['call', 'lead', 'leads', 'quote', 'estimate', 'assessment', 'bid', 'proposal', 'followup', 'follow', 'prospect', 'pitch', 'contract',
    'walkthrough', 'consult', 'consultation', 'customer', 'client', 'referral', 'outreach', 'demo'],
  marketing: ['reel', 'reels', 'video', 'post', 'posts', 'flier', 'flyer', 'content', 'caption', 'script', 'film', 'filming', 'ads', 'advert',
    'campaign', 'instagram', 'facebook', 'tiktok', 'youtube', 'newsletter', 'blog', 'seo', 'website', 'brochure', 'logo', 'thumbnail', 'hook', 'storyboard', 'shoot'],
  field: ['frame', 'framing', 'install', 'pour', 'sheath', 'sheathing', 'trim', 'drywall', 'roof', 'roofing', 'deck', 'siding', 'demo', 'demolition',
    'concrete', 'footing', 'foundation', 'insulate', 'insulation', 'stud', 'studs', 'joist', 'rafter', 'truss', 'header', 'door', 'window', 'hang',
    'lumber', 'material', 'materials', 'pickup', 'site', 'measure', 'layout', 'mud', 'tape', 'prime', 'stain', 'caulk', 'tile', 'flooring',
    'shed', 'excavate', 'backfill', 'rebar', 'shim', 'jobsite', 'cleanup', 'setup', 'paint', 'painting'],
  dev: ['fix', 'bug', 'deploy', 'mcp', 'api', 'app', 'code', 'commit', 'refactor', 'release', 'ota', 'apk', 'eas', 'server', 'vps', 'database',
    'sync', 'screen', 'endpoint', 'migration', 'render', 'typescript', 'react', 'expo', 'firebase', 'postgres', 'schema', 'crash', 'feature',
    'battleops', 'battlebooks', 'battlebids', 'bmail', 'willow', 'github', 'repo', 'dns', 'caddy', 'web', 'test', 'tests', 'build'],
  personal: ['laundry', 'groceries', 'grocery', 'dog', 'denali', 'gym', 'workout', 'doctor', 'dentist', 'haircut', 'dishes', 'vacuum', 'cook',
    'dinner', 'kids', 'family', 'birthday', 'gift', 'vet', 'pharmacy', 'prescription', 'walk', 'nap', 'sleep', 'chores', 'errand', 'errands', 'mom', 'dad'],
});
// "demo" is Sales (a product demo) in the lexicon's first pass and Field (demolition) in the second — a word in two
// roles simply votes for both; your picks settle it.

let lexiconIndex = null;
/** word (titleTokens form) → [role]. Built once. */
export function deptLexiconIndex() {
  if (lexiconIndex) return lexiconIndex;
  const m = new Map();
  for (const [role, words] of Object.entries(DEPT_LEXICON)) {
    for (const w of words) for (const tok of titleTokens(w)) { const cur = m.get(tok) || []; if (!cur.includes(role)) m.set(tok, [...cur, role]); }
  }
  lexiconIndex = m;
  return m;
}

// ---- the learned layer (meta/deptLearn) --------------------------------------------------------------------------
// One top-level field per word, `w_<word>: { <departmentId>: count }`, so two devices learning different words merge
// field by field (store §2: merge = top-level). Counts are your picks: +1 when you choose a department for a new
// task, +2 when you correct a guess (a correction should take at once). Capped at MAX_LEARN_WORDS words.
export const MAX_LEARN_WORDS = 500;
const KEY = (w) => `w_${w}`;

/** meta/deptLearn → { pair: Map(word → Map(deptId → count)), tokN: Map(word → total) } (autotag's model shape). */
export function deptLearnModel(learnDoc) {
  const pair = new Map();
  const tokN = new Map();
  for (const [k, v] of Object.entries(learnDoc || {})) {
    if (!k.startsWith('w_') || !v || typeof v !== 'object') continue;
    const m = new Map();
    let n = 0;
    for (const [d, c] of Object.entries(v)) { const x = Number(c) || 0; if (x > 0) { m.set(d, x); n += x; } }
    if (n > 0) { pair.set(k.slice(2), m); tokN.set(k.slice(2), n); }
  }
  return { pair, tokN };
}

/**
 * Teach: the title's words → `departmentId` (null = General). Returns the writes (one merge on meta/deptLearn;
 * a replace that drops the least-used words once there are more than MAX_LEARN_WORDS). [] when the title has no words.
 */
export function deptLearnWrites(learnDoc, title, departmentId, { weight = 1, now = Date.now() } = {}) {
  const words = titleTokens(title);
  if (!words.length) return [];
  const dept = departmentId || GENERAL_DEPARTMENT_ID;
  const w = Math.max(1, Math.round(Number(weight) || 1));
  const patch = {};
  for (const word of words) {
    const cur = (learnDoc && learnDoc[KEY(word)]) || {};
    patch[KEY(word)] = { ...cur, [dept]: (Number(cur[dept]) || 0) + w };
  }
  const next = { ...(learnDoc || {}), ...patch };
  const keys = Object.keys(next).filter((k) => k.startsWith('w_'));
  if (keys.length <= MAX_LEARN_WORDS) return [merge(DEPT_LEARN_PATH, { ...patch, updatedAt: now })];
  const total = (k) => Object.values(next[k] || {}).reduce((s, c) => s + (Number(c) || 0), 0);
  const keep = keys.sort((a, b) => (Object.prototype.hasOwnProperty.call(patch, b) - Object.prototype.hasOwnProperty.call(patch, a)) || total(b) - total(a)).slice(0, MAX_LEARN_WORDS);
  const doc = { updatedAt: now };
  for (const k of keep) doc[k] = next[k];
  return [set(DEPT_LEARN_PATH, doc)];
}

// ---- inference ---------------------------------------------------------------------------------------------------

const deptName = (d) => d?.name || 'General';

/** role → the first department (in order) whose name is that role. */
function roleIndex(list) {
  const m = new Map();
  for (const d of list) { const r = deptRole(d); if (r && !m.has(r)) m.set(r, d); }
  return m;
}

/**
 * The department a new task should land in, and why.
 * ctx = { departments, project (the task's project doc or null), currentDepartmentId ('all' / id), learn (meta/deptLearn
 * doc, or a deptLearnModel), opsTags, active (department mode; false → only a lock answers) }.
 * → { departmentId ('general' for General), confidence: high|medium|low, why, source, words? } or null (a subtask —
 * it follows its parent — or the mode is off and nothing locks it).
 */
export function inferTaskDepartment(task, ctx = {}) {
  if (!task || task.parentId) return null;
  const list = listDepartments(ctx.departments || []);
  const byId = new Map(list.map((d) => [d.id, d]));
  const general = byId.get(GENERAL_DEPARTMENT_ID) || { id: GENERAL_DEPARTMENT_ID, name: 'General' };
  const project = ctx.project || null;
  const out = (d, confidence, why, source, extra = {}) => ({ departmentId: d?.id || GENERAL_DEPARTMENT_ID, confidence, why, source, ...extra });
  const home = project?.departmentId && byId.get(project.departmentId) && project.departmentId !== GENERAL_DEPARTMENT_ID ? byId.get(project.departmentId) : null;

  // 1. lock
  if (isDepartmentLocked(project)) {
    const d = home || general;
    return out(d, 'high', `${project.name || 'The project'} is locked to ${deptName(d)}`, 'lock');
  }
  if (ctx.active === false) return null;
  // 2. given
  if (task.departmentId !== undefined) {
    const d = task.departmentId ? byId.get(task.departmentId) : general;
    if (d) return out(d, 'high', 'Picked', 'given');
  }
  // 3. the project sends its new tasks home
  if (home && project.newTaskDepartment === 'home') return out(home, 'high', `${project.name || 'The project'} sends new tasks to ${home.name}`, 'home');

  const roles = roleIndex(list);
  const current = ctx.currentDepartmentId && byId.get(ctx.currentDepartmentId) ? byId.get(ctx.currentDepartmentId) : null;
  const order = new Map(list.map((d, i) => [d.id, i]));
  const tieBreak = (ids) => [...ids].sort((a, b) => (b === current?.id) - (a === current?.id) || (b === home?.id) - (a === home?.id) || (order.get(a) ?? 99) - (order.get(b) ?? 99))[0];

  // 4. tags
  const idx = tagIndex(ctx.opsTags || []);
  const tagVotes = new Map();   // deptId → [tag]
  for (const raw of Array.isArray(task.tags) ? task.tags : []) {
    const slug = canonTag(resolveTag(raw, idx));
    if (!slug) continue;
    const role = DEPT_TAG_ROLES[slug] || KIND_ROLES[idx.get(slug)?.kind] || null;
    const d = role ? roles.get(role) : null;
    if (!d) continue;
    tagVotes.set(d.id, [...(tagVotes.get(d.id) || []), slug]);
  }

  // 5. words — learned beats the lexicon, word by word
  const model = ctx.learn && ctx.learn.pair instanceof Map ? ctx.learn : deptLearnModel(ctx.learn);
  const lex = deptLexiconIndex();
  const score = new Map();   // deptId → { s, words: [], learned: bool, strong: bool }
  const vote = (id, s, word, learned, strong) => {
    const cur = score.get(id) || { s: 0, words: [], learned: false, strong: false };
    cur.s += s; if (!cur.words.includes(word)) cur.words.push(word);
    cur.learned = cur.learned || learned; cur.strong = cur.strong || strong;
    score.set(id, cur);
  };
  for (const w of titleTokens(task.title)) {
    const n = model.tokN.get(w) || 0;
    let learnedHit = false;
    if (n >= 2) {
      const [[id, c]] = [...model.pair.get(w).entries()].sort((a, b) => b[1] - a[1]);
      const share = c / n;
      if (share >= 0.6 && byId.has(id)) { vote(id, 1 + share, w, true, n >= 3 && share >= 0.8); learnedHit = true; }
    }
    if (learnedHit) continue;
    for (const role of lex.get(w) || []) { const d = roles.get(role); if (d) vote(d.id, 1, w, false, false); }
  }

  if (tagVotes.size) {
    const best = Math.max(...[...tagVotes.values()].map((v) => v.length));
    const top = [...tagVotes.entries()].filter(([, v]) => v.length === best).map(([id]) => id);
    let id = top[0];
    if (top.length > 1) {
      const byWords = top.filter((x) => score.has(x)).sort((a, b) => score.get(b).s - score.get(a).s);
      id = byWords[0] || tieBreak(top);
    }
    const d = byId.get(id);
    return out(d, top.length > 1 ? 'medium' : 'high', `${tagVotes.get(id).map((t) => `#${t}`).join(' ')} → ${d.name}`, 'tag');
  }

  if (score.size) {
    const ranked = [...score.entries()].sort((a, b) => b[1].s - a[1].s);
    const [topId, top] = ranked[0];
    const second = ranked[1]?.[1]?.s || 0;
    const tied = ranked.filter(([, v]) => v.s === top.s).map(([id]) => id);
    const id = tied.length > 1 ? tieBreak(tied) : topId;
    const win = score.get(id);
    const d = byId.get(id);
    const quoted = win.words.slice(0, 3).map((x) => `“${x}”`).join(', ');
    let confidence;
    if (tied.length > 1) confidence = 'low';
    else if (!second) confidence = win.words.length >= 2 || win.strong ? 'high' : 'medium';
    else confidence = top.s >= 2 * second ? 'medium' : 'low';
    const rival = ranked.find(([rid]) => rid !== id);
    const why = `${quoted} → ${d.name}${win.learned ? ' (your picks)' : ''}${confidence === 'low' && rival ? ` · or ${byId.get(rival[0])?.name}?` : ''}`;
    return out(d, confidence, why, win.learned ? 'learned' : 'words', { words: win.words });
  }

  // 6. fallback
  if (current && current.id !== GENERAL_DEPARTMENT_ID) return out(current, 'medium', `You’re in ${current.name}`, 'current');
  if (home) return out(home, 'low', `${project.name || 'The project'}’s home department`, 'project');
  return out(general, 'low', 'Nothing to go on — General', 'none');
}

/** Sources that are a guess (stored on the task as `departmentGuess`, shown on its chip); the rest are decided. */
export const isDeptGuess = (g) => !!g && !['lock', 'given', 'home'].includes(g.source);

/**
 * What to stamp on the new task: { departmentId (null = General), departmentGuess? } — or {} to leave it alone.
 * A guess carries { confidence, why, source } so the chip can say it; a hand pick or a correction clears it.
 */
export function deptGuessStamp(guess) {
  if (!guess) return {};
  const departmentId = guess.departmentId && guess.departmentId !== GENERAL_DEPARTMENT_ID ? guess.departmentId : null;
  return isDeptGuess(guess)
    ? { departmentId, departmentGuess: { confidence: guess.confidence, why: String(guess.why || '').slice(0, 120), source: guess.source } }
    : { departmentId };
}

/** The chip's look: high / medium = the department's colour; low = muted "guess". */
export const deptChipStyle = (confidence) => (confidence === 'low' ? 'guess' : 'solid');
